import { useTranslation } from 'react-i18next'
import type { ImeiHubServiceId } from '../ImeiHubSignalIcon'
import ImeiHubServiceTitle from '../ImeiHubServiceTitle'
import { imeiHubHeroImage } from '../../utils/imeiHubHeroImage'
import {
  getVerifyLoadingSteps,
  getVerifyLoadingTitleKey,
  type VerifyLoadingStepDef,
} from '../../utils/verifyLoadingConfig'
import type { VerifyLoadingStepVisual } from '../../hooks/useVerifyLoadingProgress'
import VerifyLoadingHero from './VerifyLoadingHero'
import VerifyProgress from './VerifyProgress'
import VerifySecureFooter from './VerifySecureFooter'

export type VerifyLoadingScreenProps = {
  variant: ImeiHubServiceId
  serviceTitle: string
  imeiMasked: string
  steps?: VerifyLoadingStepDef[]
  stepVisual: (index: number) => VerifyLoadingStepVisual
  statusStepIndex: number
  readyMessage: boolean
  failed?: boolean
  errorMessage?: string | null
  onRetry?: () => void
  modal?: boolean
  titleId?: string
  savedToProfile?: boolean
  previewDemo?: boolean
  onContinueToReport?: () => void
}

export default function VerifyLoadingScreen({
  variant,
  serviceTitle,
  imeiMasked,
  steps: stepsProp,
  stepVisual,
  statusStepIndex,
  readyMessage,
  failed = false,
  errorMessage,
  onRetry,
  modal = false,
  titleId,
  savedToProfile = false,
  previewDemo = false,
  onContinueToReport,
}: VerifyLoadingScreenProps) {
  const { t } = useTranslation()
  const steps = stepsProp ?? getVerifyLoadingSteps(variant, 'single-request')
  const titleKey = getVerifyLoadingTitleKey(variant)
  const heroSrc = imeiHubHeroImage(variant)
  const isReady = readyMessage && !failed

  return (
    <div
      className={`verify-loading-screen vr-report vr-report--inline${modal ? ' verify-loading-screen--modal' : ''}${isReady ? ' verify-loading-screen--ready' : ''}`}
    >
      <header className="verify-loading-screen__head vr-report__top">
        <div className="vr-report__label-row">
          <span className="vr-report__dot" aria-hidden="true" />
          <span className="vr-report__label">{t('vc.imeiReport.label')}</span>
        </div>
        <span className="vr-report__service-pill">
          <ImeiHubServiceTitle service={variant} title={serviceTitle} variant="report" />
        </span>
      </header>

      <div className="verify-loading-screen__intro">
        <h3 id={titleId} className="verify-loading-screen__title">
          {isReady ? t('vc.verifyLoading.ready.title') : t(titleKey)}
        </h3>
        {isReady ? (
          <p className="verify-loading-screen__imei verify-loading-screen__imei--ready">{imeiMasked}</p>
        ) : (
          <>
            <p className="verify-loading-screen__subtitle">{t('vc.verifyLoading.subtitle')}</p>
            <p className="verify-loading-screen__imei">{imeiMasked}</p>
          </>
        )}
      </div>

      <div className="verify-loading-screen__stage">
        <VerifyLoadingHero heroSrc={heroSrc} serviceTitle={serviceTitle} ready={isReady} />

        <VerifyProgress
          steps={steps}
          stepVisual={stepVisual}
          statusStepIndex={statusStepIndex}
          readyMessage={readyMessage}
          failed={failed}
          errorMessage={errorMessage}
          onRetry={onRetry}
          savedToProfile={savedToProfile}
          previewDemo={previewDemo}
          onContinueToReport={onContinueToReport}
        />
      </div>

      <VerifySecureFooter />
    </div>
  )
}
